import { useEffect, useRef, useState } from "preact/hooks";

import { api } from "@/api/client";
import type { JobStatus } from "@/api/types";
import { recordingToWav } from "@/lib/audioCapture";

import { useToast } from "./ui";

export function isNativeInteractionTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return Boolean(target.closest(
    "input, textarea, select, button, a, summary, audio, video, dialog, [contenteditable]:not([contenteditable='false']), [role='dialog']",
  ));
}

export async function waitForTranscription(
  jobId: number,
  client: Pick<typeof api, "job"> = api,
  options: { intervalMs?: number; maxAttempts?: number; signal?: AbortSignal } = {},
): Promise<string> {
  const intervalMs = options.intervalMs ?? 1_000;
  const maxAttempts = options.maxAttempts ?? 120;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (options.signal?.aborted) throw new DOMException("Aborted", "AbortError");
    const job: JobStatus = await client.job(jobId);
    if (job.state === "succeeded") {
      const text = job.result?.text;
      if (typeof text !== "string" || !text.trim()) throw new Error("识别任务未返回文字，请重试");
      return text.trim();
    }
    if (job.state === "failed" || job.state === "cancelled") {
      throw new Error(job.error || "语音识别任务失败，请重试");
    }
    await new Promise<void>((resolve, reject) => {
      const abort = (): void => {
        window.clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      };
      const timer = window.setTimeout(() => {
        options.signal?.removeEventListener("abort", abort);
        resolve();
      }, intervalMs);
      options.signal?.addEventListener("abort", abort, { once: true });
    });
  }
  throw new Error("语音识别等待超时，请重试");
}

export function VoiceCommand({
  enabled,
  onIntent,
}: {
  enabled: boolean;
  onIntent: (text: string) => Promise<void>;
}) {
  const toast = useToast();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [message, setMessage] = useState("按住按钮或空格说指令；免提静音检测暂不可用");
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const chunks = useRef<Blob[]>([]);
  const requesting = useRef(false);
  const holding = useRef(false);
  const recognition = useRef<AbortController | null>(null);

  const stopTracks = (): void => {
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
  };

  useEffect(() => () => {
    holding.current = false;
    const active = recorder.current;
    if (active && active.state !== "inactive") {
      active.ondataavailable = null;
      active.onstop = null;
      active.onerror = null;
      active.stop();
    }
    recorder.current = null;
    recognition.current?.abort();
    recognition.current = null;
    stopTracks();
  }, []);

  const execute = async (value: string): Promise<void> => {
    const normalized = value.trim();
    if (!normalized) return;
    setBusy(true);
    try {
      await onIntent(normalized);
      setText("");
    } finally {
      setBusy(false);
    }
  };

  const startRecording = async (): Promise<void> => {
    if (!enabled || requesting.current || recorder.current) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      const reason = "当前浏览器不支持麦克风录音，请改用文字指令";
      setMessage(reason);
      toast(reason, "error");
      holding.current = false;
      return;
    }
    requesting.current = true;
    setBusy(true);
    setMessage("正在请求麦克风权限…");
    try {
      const nextStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!holding.current) {
        nextStream.getTracks().forEach((track) => track.stop());
        return;
      }
      stream.current = nextStream;
      chunks.current = [];
      const nextRecorder = new MediaRecorder(nextStream);
      nextRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.current.push(event.data);
      };
      recorder.current = nextRecorder;
      nextRecorder.start();
      setRecording(true);
      setMessage("录音中，松开后识别…");
    } catch {
      holding.current = false;
      recorder.current = null;
      stopTracks();
      const reason = "无法使用麦克风，请检查浏览器权限";
      setMessage(reason);
      toast(reason, "error");
    } finally {
      requesting.current = false;
      setBusy(false);
    }
  };

  const finishRecording = async (): Promise<void> => {
    const active = recorder.current;
    if (!active || active.state === "inactive") return;
    setRecording(false);
    setBusy(true);
    setMessage("正在识别指令…");
    try {
      const blob = await new Promise<Blob>((resolve, reject) => {
        active.onstop = () => resolve(new Blob(chunks.current, {
          type: active.mimeType || chunks.current[0]?.type || "audio/webm",
        }));
        active.onerror = () => reject(new Error("录音失败，请重试"));
        active.stop();
      });
      recognition.current = new AbortController();
      const signal = recognition.current.signal;
      const wav = await recordingToWav(blob);
      if (signal.aborted) throw new DOMException("Aborted", "AbortError");
      const result = await api.transcribeAudio(wav);
      if (signal.aborted) throw new DOMException("Aborted", "AbortError");
      const transcript = await waitForTranscription(result.job_id, api, {
        signal,
      });
      setText(transcript);
      setMessage(`听到：${transcript}`);
      await onIntent(transcript);
      setText("");
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      const reason = error instanceof Error ? error.message : "语音识别失败，请重试";
      setMessage(reason);
      toast(reason, "error");
    } finally {
      recorder.current = null;
      recognition.current = null;
      chunks.current = [];
      stopTracks();
      setBusy(false);
    }
  };

  const beginHold = (): void => {
    if (!enabled || holding.current) return;
    holding.current = true;
    void startRecording();
  };

  const endHold = (): void => {
    if (!holding.current) return;
    holding.current = false;
    void finishRecording();
  };

  useEffect(() => {
    const keyDown = (event: KeyboardEvent): void => {
      if (
        event.code !== "Space"
        || event.repeat
        || event.defaultPrevented
        || isNativeInteractionTarget(event.target)
        || document.querySelector("dialog[open]") !== null
      ) return;
      event.preventDefault();
      beginHold();
    };
    const keyUp = (event: KeyboardEvent): void => {
      if (event.code !== "Space" || !holding.current) return;
      event.preventDefault();
      endHold();
    };
    window.addEventListener("keydown", keyDown);
    window.addEventListener("keyup", keyUp);
    return () => {
      window.removeEventListener("keydown", keyDown);
      window.removeEventListener("keyup", keyUp);
    };
  });

  return (
    <div class="voice-command">
      <form onSubmit={(event) => { event.preventDefault(); void execute(text); }}>
        <label for="voice-command-input">语音转写指令</label>
        <div>
          <input
            id="voice-command-input"
            value={text}
            disabled={busy || recording}
            onInput={(event) => setText(event.currentTarget.value)}
            placeholder="例如：详细总结、读摘要、跳过"
          />
          <button type="submit" class="btn btn-ghost" disabled={busy || recording || !text.trim()}>
            {busy && !recording ? "处理中…" : "执行"}
          </button>
        </div>
      </form>
      <button
        type="button"
        class={`btn btn-ghost voice-hold${recording ? " recording" : ""}`}
        disabled={!enabled}
        aria-pressed={recording}
        aria-label="按住说话"
        onPointerDown={(event) => {
          event.preventDefault();
          event.currentTarget.setPointerCapture?.(event.pointerId);
          beginHold();
        }}
        onPointerUp={(event) => {
          event.currentTarget.releasePointerCapture?.(event.pointerId);
          endHold();
        }}
        onPointerCancel={endHold}
        onKeyDown={(event) => {
          if (event.code === "Space" && !event.repeat) {
            event.preventDefault();
            beginHold();
          }
        }}
        onKeyUp={(event) => {
          if (event.code === "Space") {
            event.preventDefault();
            endHold();
          }
        }}
      >
        {recording ? "松开识别" : "按住说话"}
      </button>
      <span class="voice-message" role="status" aria-live="polite">{message}</span>
    </div>
  );
}
